// Kaomoji / text-faces hub — Japanese-style emoticons grouped by mood. Each
// entry is a whole string copied verbatim (like /decorative combos and the
// Lenny faces), not a single codepoint, so it lives outside /categories. The
// dedicated Lenny Face page (/lenny-faces) is the ( ͡° ͜ʖ ͡°) sibling of this hub.

export interface KaomojiCollection {
  slug: string;
  name: string;
  /** A representative face shown on the hub card. */
  hero: string;
  tagline: string;
  intro: string[];
  faces: string[];
}

export const kaomojiCollections: KaomojiCollection[] = [
  {
    slug: "happy",
    name: "Happy Faces",
    hero: "(◕‿◕)",
    tagline: "Smiling, cheerful and grinning kaomoji for good-news moments.",
    intro: [
      "Happy kaomoji turn a plain message into a beaming little face. They read instantly as a smile and work in any text field, with no image or emoji keyboard required.",
      "Tap any face to copy the whole thing, then paste it after a sentence to set a cheerful tone.",
    ],
    faces: [
      "(◕‿◕)", "(｡◕‿◕｡)", "(＾▽＾)", "(´∀`)", "(≧▽≦)", "ヽ(´▽`)/", "(•‿•)", "(＾◡＾)",
      "( ´ ▽ ` )", "（＾ω＾）", "(☆▽☆)", "(＾‿＾)", "(´｡• ᵕ •｡`)", "(*≧ω≦*)", "(◠‿◠)", "(╹◡╹)",
    ],
  },
  {
    slug: "sad",
    name: "Sad & Crying",
    hero: "(╥﹏╥)",
    tagline: "Crying, sniffling and downcast kaomoji for when words aren't enough.",
    intro: [
      "Sad kaomoji range from a quiet pout to a full waterfall of tears. They soften bad news or add a touch of melodrama to a message.",
      "Copy a face and drop it where a frown would go — they carry the feeling without a single word.",
    ],
    faces: [
      "(╥﹏╥)", "(｡•́︿•̀｡)", "(T_T)", "(╯︵╰,)", "(；ω；)", "(•́﹏•̀)", "(T▽T)", "(ಥ﹏ಥ)",
      "(◞‸◟)", "(μ_μ)", "(πーπ)", "(ToT)", "(っ˘̩╭╮˘̩)っ", "(╯╥╰)", "(⌣́_⌣̀)", "(´；ω；`)",
    ],
  },
  {
    slug: "angry",
    name: "Angry Faces",
    hero: "(╬ Ò﹏Ó)",
    tagline: "Furious, annoyed and grumpy kaomoji for venting and mock-rage.",
    intro: [
      "Angry kaomoji range from a mild side-eye to seething, vein-popping fury. They're perfect for play-arguments and dramatic complaints.",
      "Tap one to copy it and let the little face do the shouting for you.",
    ],
    faces: [
      "(╬ Ò﹏Ó)", "(｀Д´)", "(ノಠ益ಠ)ノ", "(＃`Д´)", "(>﹏<)", "(¬､¬)", "(`A´)", "(ノ°益°)ノ",
      "(☉。☉)", "(ಠ_ಠ)", "(¬_¬)", "(～￣▽￣)～", "(҂◡_◡)", "(ﾒ｀ﾛ´)╾━╤デ╦╤─", "ヽ(`Д´)ﾉ", "(▼皿▼#)",
    ],
  },
  {
    slug: "love",
    name: "Love & Hearts",
    hero: "(♥ω♥)",
    tagline: "Lovestruck, blushing and affectionate kaomoji for sweet messages.",
    intro: [
      "Love kaomoji wear their heart on their sleeve — sparkling eyes, blushing cheeks and arms ready for a hug. They warm up a compliment or a goodnight text.",
      "Copy a face and send a little affection along with your words.",
    ],
    faces: [
      "(♥ω♥)", "(｡♥‿♥｡)", "(づ￣ ³￣)づ", "(♡˙︶˙♡)", "(ღ˘‿˘ღ)", "(♡°▽°♡)", "( ˘ ³˘)♥", "(✿◠‿◠)",
      "(◍•ᴗ•◍)❤", "♡(ŐㅅŐ｡)", "(´∩｡• ᵕ •｡∩`)", "(*♡∀♡)", "(♥￫ᴗ￩♥)", "(˘∀˘)♡", "( ͡♥ ͜ʖ ͡♥)", "(≧◡≦) ♡",
    ],
  },
  {
    slug: "cute",
    name: "Cute Faces",
    hero: "(｡◕‿‿◕｡)",
    tagline: "Adorable, soft and wholesome kaomoji that beg to be pasted everywhere.",
    intro: [
      "Cute kaomoji are all big eyes and gentle smiles — the wholesome, soft side of text faces. They make almost anything sound friendlier.",
      "Tap a face to copy it and sprinkle a little sweetness into your message.",
    ],
    faces: [
      "(｡◕‿‿◕｡)", "(◡‿◡✿)", "(´• ω •`)", "(◍•ᴗ•◍)", "(✿◕‿◕)", "(ㅅ´ ˘ `)", "(っ◔◡◔)っ", "(｡•́‿•̀｡)",
      "(´｡• ω •｡`)", "(*´ω`*)", "(｡◕ᴗ◕｡)", "(｡♡‿♡｡)", "(=ＴェＴ=)", "( ˘ ɜ˘) ♬", "(｡˃ ᵕ ˂ )", "(ᵔ◡ᵔ)",
    ],
  },
  {
    slug: "shrug",
    name: "Shrug",
    hero: "¯\\_(ツ)_/¯",
    tagline: "The classic ¯\\_(ツ)_/¯ shrug and its many variants for a casual 'meh'.",
    intro: [
      "The shrug — ¯\\_(ツ)_/¯ — is the universal text face for 'who knows' and 'not my problem'. Swap the face in the middle to change the attitude.",
      "Copy one and reply with a perfectly noncommittal shrug.",
    ],
    faces: [
      "¯\\_(ツ)_/¯", "╮(╯▽╰)╭", "┐(´∀｀)┌", "╮(╯_╰)╭", "┐(￣ヘ￣)┌", "┐(˘_˘)┌", "╮(˘_˘)╭", "¯\\_(⊙_ʖ⊙)_/¯",
      "¯\\_(ʘ_ʘ)_/¯", "┐(゜~゜)┌", "╮(╯3╰)╭", "¯\\_(°_o)_/¯", "ƪ(˘⌣˘)ʃ", "ヽ(゜～゜o)ノ",
    ],
  },
  {
    slug: "table-flip",
    name: "Table Flip",
    hero: "(╯°□°)╯︵ ┻━┻",
    tagline: "Rage-quit table flips (and a few calm put-it-back faces).",
    intro: [
      "Table-flip kaomoji are pure dramatic frustration — the table goes flying. The reverse, putting the table calmly back, is just as satisfying.",
      "Tap one to copy the whole flip in one go.",
    ],
    faces: [
      "(╯°□°)╯︵ ┻━┻", "┻━┻ ︵ヽ(`Д´)ﾉ︵ ┻━┻", "(ノಠ益ಠ)ノ彡┻━┻", "┬─┬ノ( º _ ºノ)", "(ノ-_-)ノ~┻━┻",
      "(╯`□´)╯︵┻━┻", "┳━┳ノ( ° - °ノ)", "(┛◉Д◉)┛彡┻━┻", "┬──┬ ノ(ò_óノ)", "ヽ(´ー｀)ノ︵ ┻━┻",
      "╯︵┻━┻", "(ﾉಥДಥ)ﾉ︵┻━┻", "┻━┻︵╰(°□°)╯︵┻━┻", "┬─┬ ノ( ゜-゜ノ)",
    ],
  },
  {
    slug: "surprised",
    name: "Surprised & Shocked",
    hero: "(⊙_⊙)",
    tagline: "Wide-eyed, gasping and jaw-dropped kaomoji for plot twists.",
    intro: [
      "Surprised kaomoji freeze mid-gasp with wide eyes and round mouths. They're the perfect reaction to unexpected news.",
      "Copy a stunned face and react without a single word.",
    ],
    faces: [
      "(⊙_⊙)", "(°o°)", "(ﾟοﾟ)", "(○o○)", "(⊙﹏⊙)", "(◎_◎)", "(ʘ‿ʘ)", "Σ(っ°Д°;)っ",
      "(ﾟдﾟ)", "(o_O)", "(O_O)", "(☉_☉)", "Σ(゜゜)", "(°□°)", "(#°Д°)", "(゜ロ゜)",
    ],
  },
  {
    slug: "wink",
    name: "Winking & Playful",
    hero: "(^_~)",
    tagline: "Cheeky winks and sly smirks for flirty, teasing messages.",
    intro: [
      "Winking kaomoji add a knowing, playful edge — one eye shut, a little smirk. They turn a plain line into a tease.",
      "Tap a face to copy it and send a wink along with your words.",
    ],
    faces: [
      "(^_~)", "(･ω<)", "(˘ω˘)", "(^_-)", "(｡•̀ᴗ-)✧", "(>ω^)", "(◕ ‿－)", "(^_^)b",
      "(¬‿¬)", "(~‿‿~)", "(˵ ¬ ‿ ¬ ˵)", "(･ｪ-)", "( ❛ ヮ ❛✿)", "(｡-‿-｡)", "(；一_一)",
    ],
  },
  {
    slug: "animals",
    name: "Animals (Bears & Cats)",
    hero: "ʕ•ᴥ•ʔ",
    tagline: "Bears, cats and other critter kaomoji to copy and paste.",
    intro: [
      "Animal kaomoji turn a few brackets and dots into bears, cats and other little creatures. The bear ʕ•ᴥ•ʔ and the cat (=^･ω･^=) are favourites.",
      "Copy a critter and let it tag along with your message.",
    ],
    faces: [
      "ʕ•ᴥ•ʔ", "(=^･ω･^=)", "(•ᴥ•)", "(=^‥^=)", "(^・ω・^ )", "(=｀ω´=)", "(◕ᴥ◕)", "ฅ(•ㅅ•)ฅ",
      "(=ｘェｘ=)", "(＾• ω •＾)", "ʕっ•ᴥ•ʔっ", "(・(ｪ)・)", "(=ΦωΦ=)", "ʕ •ᴥ•ʔ", "(=｀ェ´=)", "(=^ω^=)",
    ],
  },
  {
    slug: "dance",
    name: "Dancing & Music",
    hero: "♪┌(・。・)┘♪",
    tagline: "Grooving, arm-waving and music-loving kaomoji for celebrations.",
    intro: [
      "Dancing kaomoji throw their arms up and groove to the beat — great for celebrations, hype and anything music-related.",
      "Tap a face to copy the whole dance and get the party going.",
    ],
    faces: [
      "♪┌(・。・)┘♪", "ヾ(⌐■_■)ノ♪", "┏(・o･)┛", "♪♪ヽ(ˇ∀ˇ)ゞ", "＼(^o^)／", "┗(＾0＾)┓", "♬♩♪♩",
      "( ﾉ ﾟｰﾟ)ﾉ", "ヽ(♡‿♡)ノ", "＼(￣▽￣)／", "┌(★o☆)┘", "♪(┌・。・)┌", "ヽ(⌐■_■)ノ♪♬", "┌(￣ω￣)┘",
    ],
  },
  {
    slug: "greet",
    name: "Greetings & Waves",
    hero: "(・∀・)ノ",
    tagline: "Hello and goodbye kaomoji waving a friendly arm.",
    intro: [
      "Greeting kaomoji raise a friendly arm to say hi or wave goodbye. They open or close a message on a warm note.",
      "Copy a wave and greet someone without typing a word.",
    ],
    faces: [
      "(・∀・)ノ", "ヾ(＾∇＾)", "( ´ ▽ ` )ﾉ", "(´• ω •`)ﾉ", "(ﾉ´ヮ`)ﾉ*: ･ﾟ", "ノ(´ε｀●)", "( ^_^)／",
      "(=ﾟωﾟ)ノ", "(｡･ω･)ﾉﾞ", "ヾ(＾-＾)ノ", "(＊＾▽＾)／", "ヾ(´〇`)ﾉ♪", "( ﾟ▽ﾟ)/", "♪(´ε｀ )",
    ],
  },
  {
    slug: "sparkle",
    name: "Magic & Sparkle",
    hero: "(∩｀-´)⊃━☆ﾟ",
    tagline: "Sparkly, twinkling and spell-casting kaomoji full of shine.",
    intro: [
      "Sparkle kaomoji shower a message in stars and twinkles — casting little spells and adding a dreamy, magical shimmer.",
      "Tap a face to copy the whole sparkle and add some shine.",
    ],
    faces: [
      "(∩｀-´)⊃━☆ﾟ.*･｡ﾟ", "✧･ﾟ: *✧･ﾟ:*", "(☆▽☆)", "(*≧ω≦*)", "☆ミ(o*･ω･)ﾉ", "(人◕ω◕)",
      "°˖✧◝(⁰▿⁰)◜✧˖°", "(✯◡✯)", "ヽ(・∀・)ﾉ", "(￣▽￣)ノ＊・゜ﾟ・", "ヽ(*・ω・)ﾉ✧", "(づ｡◕‿‿◕｡)づ",
      "₊˚‧ (◕‿◕) ‧˚₊", "(★ω★)/",
    ],
  },
  {
    slug: "sleepy",
    name: "Sleepy & Tired",
    hero: "(－_－) zzZ",
    tagline: "Drowsy, yawning and worn-out kaomoji ready for bed.",
    intro: [
      "Sleepy kaomoji droop their eyes and trail off into zzz — perfect for goodnight messages or admitting you're exhausted.",
      "Copy a drowsy face and let it yawn on your behalf.",
    ],
    faces: [
      "(－_－) zzZ", "(￣o￣) zzZ", "(∪｡∪)｡｡｡zzz", "(-_-) zzz", "(´-ω-`)", "(︶｡︶✽)", "(=____=)",
      "(ㅍ_ㅍ)", "(≖_≖ )", "( ˘ω˘ ) zzz", "(´〜｀*) zzz", "(。-ω-)zzz", "( -.-)…zzz", "(ᴗ_ᴗ̩̩)",
    ],
  },
];

export function getKaomojiCollection(slug: string): KaomojiCollection | undefined {
  return kaomojiCollections.find((c) => c.slug === slug);
}
